import { useState } from 'react';
import { useOrg } from '../../api/app-context';
import { useSubjects } from '../../api/hooks';
import { StatusWord } from '../ui/status-word';

export interface PickedPerson {
  entity_id: string;
  name: string;
}

/**
 * **Who a report concerns** (reports design §2): a search over the
 * organization's people and the list chosen so far. The only structured input a
 * report has; everything else is the office's prose.
 *
 * It carries no research warnings. People with thin records are raised in the
 * report's preparation panel, once there is a version to prepare (ruling 11).
 */
export function PeoplePicker({
  label,
  value,
  onChange,
  max,
  accountId,
}: {
  label: string;
  value: readonly PickedPerson[];
  onChange: (next: PickedPerson[]) => void;
  max: number;
  /** An account-scoped report finds only that account's people (the account wall). */
  accountId?: string;
}) {
  const org = useOrg();
  const [q, setQ] = useState('');
  const search = useSubjects({
    q: q.trim(),
    limit: 8,
    ...(accountId ? { account_id: accountId } : {}),
  });
  const chosen = new Set(value.map((p) => p.entity_id));
  const matches =
    q.trim().length === 0
      ? []
      : (search.data?.entries ?? []).filter(
          (e) => e.level === 'tracked' && !chosen.has(e.entity_id),
        );
  const full = value.length >= max;

  return (
    <div className="report-people">
      <label className="field">
        <span className="label">{label}</span>
        <input
          className="control"
          type="search"
          placeholder={
            full ? (max === 1 ? 'One person only' : `At most ${max} people`) : 'Find a person'
          }
          value={q}
          disabled={full}
          onChange={(e) => setQ(e.target.value)}
        />
      </label>
      {matches.length > 0 ? (
        <ul className="report-people-matches" aria-label="Matching people">
          {matches.map((entry) => (
            <li key={entry.entity_id}>
              <button
                type="button"
                className="report-people-add"
                onClick={() => {
                  onChange([...value, { entity_id: entry.entity_id, name: entry.name }]);
                  setQ('');
                }}
              >
                {entry.name}
                {entry.status ? (
                  <span className="tag-faint">
                    <StatusWord vertical={org.vertical} status={entry.status} />
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {value.length > 0 ? (
        <ul className="report-people-chosen" aria-label="Chosen">
          {value.map((person) => (
            <li key={person.entity_id}>
              {person.name}
              <button
                type="button"
                className="quiet-action"
                aria-label={`Remove ${person.name}`}
                onClick={() => onChange(value.filter((p) => p.entity_id !== person.entity_id))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
